import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { Benefits, Pillars } from "../../../../../types";
import { isMissingDisplayValue } from "../../../../../formatting/string-manipulation";

export type PillarBenefitDisplayProps = {
  label: string;
  pillar: string;
  key: string;
  quantity?: string;
  description?: string;
};

function hasBenefitContent({ quantity, description }: Benefits): boolean {
  return (
    !isMissingDisplayValue(quantity?.trim()) ||
    !isMissingDisplayValue(description?.trim())
  );
}

function PillarBenefitDisplay({
  label,
  pillar,
  quantity,
  description,
}: PillarBenefitDisplayProps) {
  return (
    <Stack
      data-testid={`pillar-benefit-${pillar}`}
      spacing={{ xs: 2, md: 3 }}
      sx={{ minWidth: 0 }}
    >
      <Box
        sx={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          borderRadius: 1.5,
          backgroundColor: `pillars.${pillar}.light`,
          px: 1.5,
          py: 0.75,
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 12,
            height: 12,
            flexShrink: 0,
            borderRadius: "50%",
            backgroundColor: `pillars.${pillar}.dark`,
          }}
        />
        <Typography
          variant="inter"
          sx={{
            color: "primary.main",
            fontSize: { xs: "1rem", md: "1.15rem" },
            fontWeight: 500,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
      </Box>

      {quantity !== undefined && (
        <Typography
          variant="druk"
          data-testid={`pillar-benefit-${pillar}-quantity`}
          sx={{
            color: "primary.main",
            fontSize: { xs: "3.5rem", sm: "4.5rem", lg: "5.5rem" },
            fontWeight: 400,
            lineHeight: 0.95,
            letterSpacing: 0,
          }}
        >
          {quantity}
        </Typography>
      )}

      {description?.trim() && (
        <Typography
          variant="inter"
          data-testid={`pillar-benefit-${pillar}-description`}
          sx={{
            color: "primary.main",
            fontSize: { xs: "1rem", md: "1.15rem" },
            lineHeight: "1rem",
            maxWidth: "34ch",
          }}
        >
          {description}
        </Typography>
      )}
    </Stack>
  );
}

function PillarBenefits({ pillars }: { pillars: Pillars }) {
  const pillarNames = ["carbon", "biodiversity", "people"] as const;
  const benefits = pillarNames
    .map((pillar) => ({
      label: pillar.charAt(0).toUpperCase() + pillar.slice(1),
      pillar,
      quantity: pillars?.[pillar]?.benefits?.quantity,
      description: pillars?.[pillar]?.benefits?.description,
    }))
    .filter(hasBenefitContent);

  return (
    <Tooltip
      followCursor
      title="This component demonstrates how these benefits will appear on the earthly.org website"
    >
      <Box
        data-testid="pillar-benefits"
        sx={{
          width: "100%",
          minWidth: 0,
          maxWidth: "800px",
          borderRadius: 2,
          backgroundColor: "background.tabCard",
          px: { xs: 2.5, sm: 3.5 },
          py: { xs: 3, sm: 4 },
          border: "solid 3px",
          borderColor: "background.banner",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "repeat(3, minmax(0, 1fr))",
            },
            columnGap: { md: 6, lg: 9 },
            rowGap: 5,
          }}
        >
          {benefits.map((benefit) => (
            <PillarBenefitDisplay key={benefit.pillar} {...benefit} />
          ))}
        </Box>
      </Box>
    </Tooltip>
  );
}

export default PillarBenefits;
